import { useMemo, useState } from "react";
import type { Identifier } from "ra-core";

import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  buildFramingStageViews,
  type FramingSceneInspector,
  type FramingStageKey,
} from "../framingInspector";
import { useFramingSceneInspector } from "../useFramingSceneInspector";
import { FramingStagePane, buildStagePaneModel } from "./FramingStagePane";
import {
  comparisonEligibility,
  diffAttemptSummaries,
} from "./framingAttemptDiff";
import type { FramingViewportCamera } from "./framingViewportMath";

/**
 * Same-stage attempt comparison (CROP-WB-011). Attempt B loads lazily
 * through the ordinary selected-attempt endpoint; side-by-side and field
 * deltas are always available, while wipe-style alignment stays disabled
 * unless the stage is geometrically comparable.
 */
export function FramingAttemptComparison({
  sessionId,
  inspectorA,
  attemptB,
  stage,
  timeSeconds,
}: {
  sessionId: Identifier;
  inspectorA: FramingSceneInspector;
  attemptB: string;
  stage: FramingStageKey;
  timeSeconds: number;
}) {
  const [stageKey, setStageKey] = useState<FramingStageKey>(stage);
  const [cameraA, setCameraA] = useState<FramingViewportCamera | null>(null);
  const [cameraB, setCameraB] = useState<FramingViewportCamera | null>(null);
  const inspectorBQuery = useFramingSceneInspector({
    sessionId,
    sceneId: inspectorA.sceneId,
    attemptId: attemptB,
    enabled: true,
  });
  const inspectorB = inspectorBQuery.data ?? null;

  const stagesA = useMemo(
    () => buildFramingStageViews(inspectorA),
    [inspectorA],
  );
  const stagesB = useMemo(
    () => (inspectorB ? buildFramingStageViews(inspectorB) : []),
    [inspectorB],
  );
  const stageA = stagesA.find((view) => view.key === stageKey) ?? null;
  const stageB = stagesB.find((view) => view.key === stageKey) ?? null;
  const availableStages = stagesA.filter((view) =>
    stagesB.some((other) => other.key === view.key),
  );
  const eligibility = comparisonEligibility({
    stageA: stageA?.key ?? null,
    stageB: stageB?.key ?? null,
    spaceA: stageA?.coordinateSpace ?? null,
    spaceB: stageB?.coordinateSpace ?? null,
    durationA: inspectorA.appliedReceipt?.durationSeconds,
    durationB: inspectorB?.appliedReceipt?.durationSeconds,
  });

  const summaryA = inspectorA.attempts.find(
    (attempt) =>
      attempt.attemptId ===
      (inspectorA.attemptId ?? inspectorA.current.attemptId),
  );
  const summaryB = inspectorA.attempts.find(
    (attempt) => attempt.attemptId === attemptB,
  );
  const deltas =
    summaryA && summaryB ? diffAttemptSummaries(summaryA, summaryB) : [];

  return (
    <section aria-label="Attempt comparison" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">Comparing at stage</span>
        <Select
          value={stageKey}
          onValueChange={(value) => setStageKey(value as FramingStageKey)}
        >
          <SelectTrigger
            size="sm"
            className="min-w-44"
            aria-label="Comparison stage"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(availableStages.length > 0 ? availableStages : stagesA).map(
              (view) => (
                <SelectItem key={view.key} value={view.key}>
                  {view.label}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        {!eligibility.wipe && eligibility.reason ? (
          <Badge variant="outline">{eligibility.reason}</Badge>
        ) : null}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {stageA ? (
          <div>
            <p className="mb-1 text-xs font-medium">
              Attempt{" "}
              {inspectorA.attemptId ?? inspectorA.current.attemptId ?? "A"}
            </p>
            <FramingStagePane
              model={buildStagePaneModel(
                inspectorA,
                stageA,
                "plan_vs_applied",
                timeSeconds,
              )}
              camera={cameraA}
              onCameraChange={setCameraA}
              timeSeconds={timeSeconds}
            />
          </div>
        ) : null}
        {inspectorBQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading attempt B…</p>
        ) : inspectorB && stageB ? (
          <div>
            <p className="mb-1 text-xs font-medium">Attempt {attemptB}</p>
            <FramingStagePane
              model={buildStagePaneModel(
                inspectorB,
                stageB,
                "plan_vs_applied",
                timeSeconds,
              )}
              camera={cameraB}
              onCameraChange={setCameraB}
              timeSeconds={timeSeconds}
            />
          </div>
        ) : inspectorBQuery.isError ? (
          <p className="text-sm text-destructive">
            Attempt B could not be loaded; its evidence may have been deleted.
            The accepted attempt was not substituted.
          </p>
        ) : null}
      </div>
      {deltas.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-xs">
            <caption className="sr-only">Field deltas between attempts</caption>
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th scope="col" className="py-1 pr-2">
                  Field
                </th>
                <th scope="col" className="py-1 pr-2">
                  Attempt A
                </th>
                <th scope="col" className="py-1 pr-2">
                  Attempt B
                </th>
              </tr>
            </thead>
            <tbody>
              {deltas
                .filter((delta) => delta.changed)
                .map((delta) => (
                  <tr key={delta.field} className="border-b">
                    <th scope="row" className="py-1 pr-2 text-left font-normal">
                      {delta.field}
                    </th>
                    <td className="py-1 pr-2 font-mono">{delta.a ?? "—"}</td>
                    <td className="py-1 pr-2 font-mono">{delta.b ?? "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
